import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Combobox } from '@/components/ui/combobox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Users, Plus, X, UserPlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import { parseRniText } from '@/lib/rniParser';

const emptyNuevo = {
  rut: '', nombres: '', primer_apellido: '', segundo_apellido: '', fecha_nacimiento: '',
};

export const FamiliaAtencionSection = ({
  familiaMiembros,
  miembros,
  setMiembros,
  vacunasConPrecio,
  dosisGenerales,
  formatCurrency,
  contactoTitular,
}) => {
  const [nuevoOpen, setNuevoOpen] = useState(false);
  const [nuevo, setNuevo] = useState(emptyNuevo);
  const [buscando, setBuscando] = useState(false);
  const [encontrado, setEncontrado] = useState(null); // paciente doc from DB
  const [rniText, setRniText] = useState('');

  const disponibles = familiaMiembros.filter(f => !miembros.some(m => m.paciente.id === f.id));

  // Debounced DB search by RUT (same behaviour as titular)
  useEffect(() => {
    if (!nuevoOpen) return;
    const cleaned = (nuevo.rut || '').replace(/[^0-9kK]/gi, '').toUpperCase();
    if (cleaned && cleaned.length >= 7) {
      const timeoutId = setTimeout(async () => {
        setBuscando(true);
        try {
          const r = await axios.get(`${API}/pacientes/buscar?rut=${encodeURIComponent(nuevo.rut.trim())}`, {
            withCredentials: true,
          });
          if (r.data.encontrado && r.data.paciente) {
            const p = r.data.paciente;
            setEncontrado(p);
            setNuevo(n => ({
              ...n,
              rut: p.rut || n.rut,
              nombres: p.nombres || n.nombres,
              primer_apellido: p.primer_apellido || n.primer_apellido,
              segundo_apellido: p.segundo_apellido || n.segundo_apellido,
              fecha_nacimiento: p.fecha_nacimiento || n.fecha_nacimiento,
            }));
            toast.success('Familiar encontrado en la base de datos - datos cargados');
          } else {
            setEncontrado(null);
          }
        } catch (e) {
          console.error('Error searching familiar:', e);
          setEncontrado(null);
        } finally {
          setBuscando(false);
        }
      }, 500);
      return () => clearTimeout(timeoutId);
    } else {
      setEncontrado(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nuevo.rut, nuevoOpen]);

  const extraerRni = () => {
    const datos = parseRniText(rniText) || {};
    if (Object.keys(datos).length > 0) {
      setNuevo(n => ({ ...n, ...datos }));
      toast.success('Datos del familiar extraídos del RNI');
    } else {
      toast.info('No se pudieron extraer datos. Verifique el formato del texto pegado.');
    }
  };

  const resetNuevo = () => {
    setNuevo(emptyNuevo);
    setEncontrado(null);
    setRniText('');
    setNuevoOpen(false);
  };

  const addMiembro = (f) => {
    setMiembros([...miembros, { paciente: f, vacunas: [], monto: 0 }]);
  };

  const addNuevoMiembro = () => {
    if (!nuevo.nombres.trim() || !nuevo.primer_apellido.trim()) {
      toast.error('Nombres y primer apellido del familiar son requeridos');
      return;
    }
    if (!nuevo.fecha_nacimiento) {
      toast.error('La fecha de nacimiento del familiar es requerida');
      return;
    }
    if (!nuevo.rut.trim()) {
      toast.warning('Sin RUT, el familiar se vacunará pero no quedará guardado en la base de Pacientes');
    }
    const rutNuevo = nuevo.rut.trim();
    if (rutNuevo && miembros.some(m => (m.paciente.rut || '').replace(/[.\s]/g, '') === rutNuevo.replace(/[.\s]/g, ''))) {
      toast.error('Ese familiar ya está agregado a la atención');
      return;
    }
    const nombreCompleto = [nuevo.nombres, nuevo.primer_apellido, nuevo.segundo_apellido]
      .filter(Boolean).join(' ').toUpperCase();
    const base = encontrado || {};
    const paciente = {
      id: base.id || `nuevo-${crypto.randomUUID()}`,
      esNuevo: !base.id,
      rut: rutNuevo || base.rut || '',
      pasaporte: base.pasaporte || '',
      otro_documento: base.otro_documento || '',
      nombres: nuevo.nombres.trim().toUpperCase(),
      primer_apellido: nuevo.primer_apellido.trim().toUpperCase(),
      segundo_apellido: (nuevo.segundo_apellido || '').trim().toUpperCase(),
      nombre_completo: nombreCompleto,
      fecha_nacimiento: nuevo.fecha_nacimiento,
      comuna: base.comuna || contactoTitular?.comuna || '',
      telefono: base.telefono || contactoTitular?.telefono || '',
      email: base.email || contactoTitular?.email || '',
    };
    setMiembros([...miembros, { paciente, vacunas: [], monto: 0 }]);
    resetNuevo();
    toast.success(base.id
      ? `${nombreCompleto} agregado a la atención`
      : `${nombreCompleto} agregado a la atención (se registrará como familiar)`);
  };

  const removeMiembro = (id) => {
    setMiembros(miembros.filter(m => m.paciente.id !== id));
  };

  const updateMiembro = (id, updater) => {
    setMiembros(miembros.map(m => (m.paciente.id === id ? updater(m) : m)));
  };

  const addVacuna = (id, nombre) => {
    const obj = vacunasConPrecio.find(v => v.value === nombre);
    if (!obj) return;
    updateMiembro(id, m => {
      if (m.vacunas.some(v => v.value === nombre)) return m;
      const vacunas = [...m.vacunas, { ...obj, dosisSeleccionada: '' }];
      return { ...m, vacunas, monto: vacunas.reduce((s, v) => s + (v.precio || 0), 0) };
    });
  };

  const removeVacuna = (id, nombre) => {
    updateMiembro(id, m => {
      const vacunas = m.vacunas.filter(v => v.value !== nombre);
      return { ...m, vacunas, monto: vacunas.reduce((s, v) => s + (v.precio || 0), 0) };
    });
  };

  const setDosis = (id, nombre, dosis) => {
    updateMiembro(id, m => ({
      ...m,
      vacunas: m.vacunas.map(v => (v.value === nombre ? { ...v, dosisSeleccionada: dosis } : v)),
    }));
  };

  const setMonto = (id, monto) => {
    updateMiembro(id, m => ({ ...m, monto: parseFloat(monto) || 0 }));
  };

  return (
    <Card className="mb-6 border-purple-200" data-testid="familia-atencion-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2 text-purple-800">
          <Users className="w-5 h-5" />
          Atención Familiar
        </CardTitle>
        <p className="text-sm text-slate-500">
          {familiaMiembros.length > 0
            ? 'Este paciente tiene familiares registrados. Puedes atenderlos en esta misma atención con un solo cobro.'
            : '¿Viene en familia? Agrega a los familiares para atenderlos juntos con un solo cobro. Se registrarán automáticamente como familia.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Available registered family members */}
        {disponibles.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {disponibles.map(f => (
              <Button
                key={f.id}
                type="button"
                variant="outline"
                size="sm"
                className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                onClick={() => addMiembro(f)}
                data-testid={`add-familiar-${f.id}`}
              >
                <Plus className="w-3 h-3" />
                {f.nombre_completo}
              </Button>
            ))}
          </div>
        )}

        {/* Add NEW family member */}
        {!nuevoOpen ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-2 border-dashed border-purple-300 text-purple-700 hover:bg-purple-50"
            onClick={() => setNuevoOpen(true)}
            data-testid="btn-nuevo-familiar"
          >
            <UserPlus className="w-4 h-4" />
            Agregar familiar
          </Button>
        ) : (
          <div className="border border-purple-300 border-dashed rounded-lg p-4 space-y-3 bg-purple-50/30" data-testid="form-nuevo-familiar">
            <div className="flex items-center justify-between">
              <p className="text-sm font-semibold text-purple-800 flex items-center gap-2">
                <UserPlus className="w-4 h-4" />
                Agregar familiar — busca por RUT o pega los datos del RNI
              </p>
              <Button type="button" variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={resetNuevo}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* RNI paste module */}
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
              <Label className="text-xs text-amber-800 font-medium">Pegar datos del RNI (opcional)</Label>
              <Textarea
                value={rniText}
                onChange={(e) => setRniText(e.target.value)}
                placeholder="Pegue aquí los datos del familiar copiados del RNI..."
                className="min-h-[60px] bg-white border-amber-300 text-sm"
                data-testid="input-rni-familiar"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={extraerRni}
                disabled={!rniText.trim()}
                className="text-amber-700 border-amber-300 hover:bg-amber-100 h-7 text-xs"
                data-testid="btn-extraer-rni-familiar"
              >
                Extraer Datos
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">RUT (busca en la base de datos)</Label>
                <div className="relative">
                  <Input
                    value={nuevo.rut}
                    onChange={(e) => setNuevo(n => ({ ...n, rut: e.target.value }))}
                    placeholder="12345678-9"
                    className={`h-9 pr-8 ${encontrado ? 'border-green-500 bg-green-50' : ''}`}
                    data-testid="input-nuevo-familiar-rut"
                  />
                  {buscando && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
                    </div>
                  )}
                  {!buscando && encontrado && (
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 text-green-500 text-sm">✓</div>
                  )}
                </div>
                {encontrado && (
                  <p className="text-xs text-green-600 mt-1 flex items-center gap-1" data-testid="familiar-encontrado-msg">
                    <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                    Encontrado en registros anteriores
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs">Nombres *</Label>
                <Input
                  value={nuevo.nombres}
                  onChange={(e) => setNuevo(n => ({ ...n, nombres: e.target.value }))}
                  className="h-9"
                  data-testid="input-nuevo-familiar-nombres"
                />
              </div>
              <div>
                <Label className="text-xs">Fecha de nacimiento *</Label>
                <Input
                  type="date"
                  value={nuevo.fecha_nacimiento}
                  onChange={(e) => setNuevo(n => ({ ...n, fecha_nacimiento: e.target.value }))}
                  className="h-9"
                  data-testid="input-nuevo-familiar-fnac"
                />
              </div>
              <div>
                <Label className="text-xs">Primer apellido *</Label>
                <Input
                  value={nuevo.primer_apellido}
                  onChange={(e) => setNuevo(n => ({ ...n, primer_apellido: e.target.value }))}
                  className="h-9"
                  data-testid="input-nuevo-familiar-apellido1"
                />
              </div>
              <div>
                <Label className="text-xs">Segundo apellido</Label>
                <Input
                  value={nuevo.segundo_apellido}
                  onChange={(e) => setNuevo(n => ({ ...n, segundo_apellido: e.target.value }))}
                  className="h-9"
                  data-testid="input-nuevo-familiar-apellido2"
                />
              </div>
              <div className="flex items-end">
                <Button
                  type="button"
                  size="sm"
                  className="bg-purple-600 hover:bg-purple-700 gap-1 w-full"
                  onClick={addNuevoMiembro}
                  data-testid="btn-confirmar-nuevo-familiar"
                >
                  <Plus className="w-4 h-4" />
                  Agregar a la atención
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Added members */}
        {miembros.map(m => (
          <div
            key={m.paciente.id}
            className="border border-purple-200 bg-purple-50/50 rounded-lg p-4 space-y-3"
            data-testid={`miembro-card-${m.paciente.id}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-slate-900">{m.paciente.nombre_completo}</p>
                  {m.paciente.esNuevo && (
                    <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200 text-[10px] py-0">
                      Nuevo
                    </Badge>
                  )}
                </div>
                <p className="text-xs text-slate-500 font-mono">
                  {m.paciente.rut || m.paciente.pasaporte || m.paciente.otro_documento || 'Sin documento'}
                  {m.paciente.fecha_nacimiento ? ` · ${m.paciente.fecha_nacimiento}` : ''}
                </p>
                {!m.paciente.fecha_nacimiento && (
                  <p className="text-xs text-amber-600 mt-0.5">
                    ⚠ Este familiar no tiene fecha de nacimiento registrada (requerida)
                  </p>
                )}
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 w-7 p-0 text-red-600 hover:bg-red-50"
                onClick={() => removeMiembro(m.paciente.id)}
                data-testid={`remove-miembro-${m.paciente.id}`}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Vaccine selector */}
            <Combobox
              data-testid={`select-vacuna-miembro-${m.paciente.id}`}
              value=""
              onChange={(value) => addVacuna(m.paciente.id, value)}
              options={vacunasConPrecio
                .filter(v => !m.vacunas.some(x => x.value === v.value))
                .map(v => ({
                  value: v.value,
                  label: `${v.value}${v.precio ? ` — ${formatCurrency(v.precio)}` : ''}`,
                }))}
              placeholder="+ Agregar vacuna para este familiar..."
              searchPlaceholder="Buscar vacuna..."
              emptyText="Sin coincidencias"
            />

            {m.vacunas.map(v => {
              const dosisOpciones = v.dosis_disponibles && v.dosis_disponibles.length > 0
                ? v.dosis_disponibles
                : dosisGenerales;
              return (
                <div key={v.value} className="bg-white border border-purple-100 rounded-lg p-2 flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-medium flex-1 min-w-[140px]">{v.value}</span>
                  <span className="text-xs text-emerald-600 font-medium">
                    {v.precio ? formatCurrency(v.precio) : ''}
                  </span>
                  <Select
                    value={v.dosisSeleccionada || ''}
                    onValueChange={(d) => setDosis(m.paciente.id, v.value, d)}
                  >
                    <SelectTrigger className="h-8 text-sm w-44" data-testid={`select-dosis-miembro-${m.paciente.id}-${v.id}`}>
                      <SelectValue placeholder="Dosis..." />
                    </SelectTrigger>
                    <SelectContent>
                      {dosisOpciones.map(d => (
                        <SelectItem key={d} value={d}>{d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-red-500 hover:bg-red-50"
                    onClick={() => removeVacuna(m.paciente.id, v.value)}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              );
            })}

            {m.vacunas.length > 0 && (
              <div className="flex items-center gap-2 justify-end">
                <span className="text-xs text-slate-600">Monto ($):</span>
                <Input
                  type="number"
                  value={m.monto}
                  onChange={(e) => setMonto(m.paciente.id, e.target.value)}
                  className="w-32 h-8 text-sm"
                  data-testid={`input-monto-miembro-${m.paciente.id}`}
                />
              </div>
            )}
          </div>
        ))}

        {miembros.length > 0 && (
          <div className="flex items-center justify-between bg-purple-100 border border-purple-200 rounded-lg p-3">
            <span className="text-sm font-semibold text-purple-800">
              Subtotal familiares ({miembros.length}):
            </span>
            <span className="text-lg font-bold text-purple-700" data-testid="subtotal-familiares">
              {formatCurrency(miembros.reduce((s, m) => s + (m.monto || 0), 0))}
            </span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
